import { useContext, useMemo } from 'react'

import { isArray, isFunction } from 'lodash'

import { GlobalContext } from '../../../../Providers/GlobalProvider'
import EntitiesSelector from '../EntitiesSelector'
import EntityOptionLabel from '../EntityOptionLabel'
import { isObject } from '../../../../utils'
import { InstitutionModalContext } from '../../../../Providers/Forms/InstitutionModal/InstitutionModal'
import { InstitutionGroupContext } from '../../../../Providers/InstitutionGroupProvider'
import { getEntityRank } from '../../Badges/EntityBadge'

const getLabel = (institution, reverse) => (reverse ? institution.name : institution.acronym ?? institution.name) ?? ''

const getTooltip = (institution, reverse) => reverse ? institution.acronym ?? institution.name : institution.name

/**
 * The institutions are listed flat, the badged ones first : premium, then managed, then verified and
 * finally the ones carrying no badge, each category sorted alphabetically. The badge of every option
 * is what tells the categories apart.
 *
 * @param {function} isDisabled tells the institutions that cannot be picked, listed all the same
 * @param {string} selectorClassName the wrapping selects being free to grow is not what every caller
 * wants : the internship card, for one, holds its selectors on a single compact line
 */
const InstitutionsSelector = ({
  className = '', disabled, dropdownRender, empty = false, institutions, multiple = 'default', placeholder = '', reverse = false, style = {}, values,
  renderOption, onChange, onSearch, loading, isDisabled, selectorClassName = 'entities-selector-custom'
}) => {
  const gc = useContext(GlobalContext)
  const modalContext = useContext(InstitutionModalContext)
  const ig = useContext(InstitutionGroupContext)

  const institutionEntities = useMemo(() => {
    let institutionEntities = []

    if (isArray(institutions)) {
      institutionEntities = institutions
    } else if (isObject(ig)) {
      institutionEntities = ig.institutions
    } else if (isObject(gc)) {
      institutionEntities = gc.institutions
    }

    return institutionEntities
      .filter(i => isObject(i) && i.id !== null && i.id !== undefined)
      .sort((a, b) => {
        const rankDiff = getEntityRank(a) - getEntityRank(b)

        if (rankDiff !== 0) {
          return rankDiff
        }

        return getLabel(a, reverse).localeCompare(getLabel(b, reverse))
      })
  }, [institutions, gc, ig, reverse])

  const institutionsOptions = useMemo(() => institutionEntities.map(institution => ({
    label: getLabel(institution, reverse),
    value: institution.id,
    tooltip: getTooltip(institution, reverse),
    disabled: isFunction(isDisabled) && isDisabled(institution),
    entity: institution
  })), [institutionEntities, reverse, isDisabled])

  return (
    <div className={'flex-col w-100 ' + className}>
      <EntitiesSelector
        selectorClassName={selectorClassName}
        disabled={disabled}
        empty={empty}
        entityOptions={institutionsOptions}
        multiple={multiple}
        placeholder={placeholder}
        showSearch
        style={style}
        values={values}
        loading={loading}
        dropdownRender={
          modalContext !== undefined && isFunction(dropdownRender)
            ? dropdownRender(modalContext.setFormHandler)
            : undefined
        }
        onChange={onChange}
        onSearch={onSearch}
        renderOption={
          isFunction(renderOption)
            ? renderOption
            : institution => <EntityOptionLabel entity={institution} label={getLabel(institution, reverse)} />
        }
      />
    </div>
  )
}

export default InstitutionsSelector
